import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const categories = ['All', 'Guides', 'Articles', 'Tools'];

const resources = [
  { cat: 'Guides', title: 'Business Insurance Buyer\'s Guide', desc: 'Everything Appalachian business owners need to know before purchasing commercial coverage.', time: '8 min read', date: 'Mar 2026', image: 'https://public.readdy.ai/ai/img_res/45677d62d6c83bff4825794007387e26.jpg', link: '/resources/business-insurance-buyers-guide' },
  { cat: 'Articles', title: 'Understanding Workers\' Comp in KY, TN & VA', desc: 'State-by-state breakdown of workers\' compensation requirements for Tri-State employers.', time: '6 min read', date: 'Feb 2026', image: 'https://readdy.ai/api/search-image?query=construction%20workers%20safety%20equipment%20job%20site%20Appalachian%20professional%20photography%20industrial%20tones&width=500&height=300&seq=res2026b&orientation=landscape', link: '/resources/workers-comp-ky-tn-va' },
  { cat: 'Tools', title: 'Coverage Estimator', desc: 'Use our quick tool to estimate how much general liability or property coverage your business needs.', time: '3 min', date: 'Jan 2026', image: 'https://readdy.ai/api/search-image?query=professional%20person%20using%20laptop%20calculator%20financial%20planning%20office%20neutral%20background%20warm%20lighting%20photography&width=500&height=300&seq=res2026c&orientation=landscape', link: '/resources/coverage-estimator' },
  { cat: 'Guides', title: 'Contractor Insurance Checklist', desc: 'A complete checklist to make sure your contracting business has all the coverage it needs.', time: '5 min read', date: 'Dec 2025', image: 'https://readdy.ai/api/search-image?query=contractor%20reviewing%20checklist%20clipboard%20construction%20site%20professional%20photography%20industrial%20Appalachian%20setting&width=500&height=300&seq=res2026d&orientation=landscape', link: '/resources/contractor-insurance-checklist' },
  { cat: 'Articles', title: 'Coal Mine Insurance: What\'s Required?', desc: 'A deep dive into the specialized coverage requirements for coal mining operations in Appalachia.', time: '7 min read', date: 'Nov 2025', image: 'https://readdy.ai/api/search-image?query=coal%20mining%20operation%20Appalachian%20heavy%20equipment%20industrial%20landscape%20dramatic%20photography%20wide%20angle%20dark%20tones&width=500&height=300&seq=res2026e&orientation=landscape', link: '/resources/coal-mine-insurance' },
  { cat: 'Guides', title: 'How to File a Claim: Step by Step', desc: 'A simple walkthrough of our claims process so you know exactly what to expect when you need us most.', time: '4 min read', date: 'Oct 2025', image: 'https://readdy.ai/api/search-image?query=professional%20insurance%20agent%20helping%20client%20office%20setting%20friendly%20warm%20lighting%20professional%20photography%20neutral%20background&width=500&height=300&seq=res2026f&orientation=landscape', link: '/resources/how-to-file-a-claim' },
];

export default function Resources() {
  const [activeCategory, setActiveCategory] = useState('All');
  const filtered = activeCategory === 'All' ? resources : resources.filter(r => r.cat === activeCategory);

  useEffect(() => {
    document.title = 'Insurance Resources & Guides | Summit Appalachia Insurance';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${import.meta.env.VITE_SITE_URL}/resources#webpage`,
      url: `${import.meta.env.VITE_SITE_URL}/resources`,
      name: 'Insurance Resources & Guides | Summit Appalachia Insurance',
      description: 'Free insurance guides, articles, and tools for Appalachian business owners. Learn about workers comp, contractor insurance, coal mine coverage, and more.',
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
      hasPart: resources.map(r => ({
        '@type': 'Article',
        name: r.title,
        description: r.desc,
        url: `${import.meta.env.VITE_SITE_URL}${r.link}`,
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-resources';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-resources')?.remove(); };
  }, []);

  return (
    <div className="font-sans bg-[#0a0a0a]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[380px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://readdy.ai/api/search-image?query=open%20book%20documents%20wooden%20desk%20mountain%20view%20window%20warm%20afternoon%20light%20cozy%20study%20atmosphere%20professional%20photography%20neutral%20tones&width=1920&height=480&seq=reshero2026&orientation=landscape"
            alt="Resources" className="w-full h-full object-cover object-top opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-16 w-full">
          <p className="text-white/30 text-xs font-sans mb-3 uppercase tracking-widest">
            <Link to="/" className="hover:text-[#D4AF37] cursor-pointer">Home</Link> / Resources
          </p>
          <h1 className="font-serif text-5xl md:text-7xl text-white uppercase">Knowledge Center</h1>
        </div>
      </section>

      {/* Filter */}
      <section className="py-10 bg-[#111111] border-b border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-wrap gap-3">
          {categories.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-md text-xs font-bold uppercase tracking-widest font-sans transition-colors cursor-pointer whitespace-nowrap ${activeCategory === cat ? 'bg-[#D4AF37] text-black' : 'bg-[#1a1a1a] text-white/50 border border-white/10 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]'}`}>
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((r, i) => {
              const cardContent = (
                <>
                  <div className="w-full h-44 overflow-hidden">
                    <img src={r.image} alt={r.title} className="w-full h-full object-cover object-top opacity-80 group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest font-sans">{r.cat}</span>
                    <h3 className="font-serif text-lg text-white mt-2 mb-2">{r.title}</h3>
                    <p className="text-white/40 text-sm font-sans leading-relaxed mb-4">{r.desc}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-white/25 text-xs font-sans">{r.time} · {r.date}</span>
                      <span className={`text-xs font-bold font-sans flex items-center gap-1 uppercase tracking-wider ${r.link ? 'text-[#D4AF37]' : 'text-white/20'}`}>
                        Read <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </div>
                </>
              );

              return r.link ? (
                <Link key={i} to={r.link}
                  className="bg-[#111111] border border-[#D4AF37]/15 rounded-md overflow-hidden group hover:border-[#D4AF37]/40 transition-colors cursor-pointer block">
                  {cardContent}
                </Link>
              ) : (
                <div key={i} className="bg-[#111111] border border-[#D4AF37]/15 rounded-md overflow-hidden group hover:border-[#D4AF37]/40 transition-colors cursor-pointer">
                  {cardContent}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
